import { useMemo } from 'react';
import type {} from '@react-three/fiber';
import * as THREE from 'three';
import { PIECE_PARTS, pieceTypeOf, type PiecePart } from './piece-parts.js';
import {
  DEFAULT_PIECE_MATERIAL,
  DEFAULT_PIECE_BLACK_COLOR,
  DEFAULT_PIECE_WHITE_COLOR,
  type PieceMaterialSpec,
} from './materials.js';
import { DEFAULT_PIECE_SIZE, type PieceSizeSpec } from './sizes.js';
import type { PieceTheme } from './themes.js';

function geometryFor(part: PiecePart): THREE.BufferGeometry {
  switch (part.kind) {
    case 'box':
      return new THREE.BoxGeometry(part.args[0], part.args[1], part.args[2]);
    case 'sphere':
      return new THREE.SphereGeometry(part.args[0], 12, 10);
    case 'cone':
      return new THREE.ConeGeometry(part.args[0], part.args[1], part.args[2]);
    case 'cylinder':
      return new THREE.CylinderGeometry(part.args[0], part.args[1], part.args[2], 16);
  }
}

function Part({
  part,
  color,
  material,
  size,
}: {
  part: PiecePart;
  color: string;
  material: PieceMaterialSpec;
  size: PieceSizeSpec;
}) {
  const geometry = useMemo(() => geometryFor(part), [part]);
  // The size scale multiplies each part's authored scale (defaulting to 1,1,1
  // when the part omits one), so all four piece types (pawn / rook / bishop /
  // knight) grow or shrink uniformly.
  const composedScale = useMemo<[number, number, number]>(() => {
    const s = part.scale ?? [1, 1, 1];
    return [s[0] * size.scale, s[1] * size.scale, s[2] * size.scale];
  }, [part.scale, size.scale]);
  return (
    <mesh geometry={geometry} position={part.pos} scale={composedScale}>
      <meshStandardMaterial
        color={color}
        roughness={material.roughness}
        metalness={material.metalness}
        emissive={material.emissive ?? '#000000'}
        emissiveIntensity={material.emissiveIntensity ?? 0}
      />
    </mesh>
  );
}

/**
 * Pick the material color for a piece based on its FEN char (uppercase = white,
 * lowercase = black) and the caller-supplied per-side hex. The theme is kept
 * on the signature only for back-compat — color is always the per-side prop.
 */
export function pieceColor(
  char: string,
  whiteColor: string,
  blackColor: string,
  _theme: PieceTheme,
): string {
  return char === char.toUpperCase() ? whiteColor : blackColor;
}

export function PieceMesh({
  char,
  position,
  theme = { id: 'classic', name: 'Classic', white: DEFAULT_PIECE_WHITE_COLOR, black: DEFAULT_PIECE_BLACK_COLOR, preview: '' },
  material = DEFAULT_PIECE_MATERIAL,
  size = DEFAULT_PIECE_SIZE,
  whiteColor = DEFAULT_PIECE_WHITE_COLOR,
  blackColor = DEFAULT_PIECE_BLACK_COLOR,
}: {
  char: string;
  position: [number, number, number];
  /** Kept for back-compat with callers that only know the legacy PieceTheme.
   *  Color is sourced from `whiteColor`/`blackColor` instead. */
  theme?: PieceTheme;
  material?: PieceMaterialSpec;
  size?: PieceSizeSpec;
  whiteColor?: string;
  blackColor?: string;
}) {
  const type = pieceTypeOf(char);
  const color = pieceColor(char, whiteColor, blackColor, theme);
  return (
    <group position={position}>
      {PIECE_PARTS[type].map((part, i) => (
        <Part
          key={`${part.kind}-${i}`}
          part={part}
          color={color}
          material={material}
          size={size}
        />
      ))}
    </group>
  );
}
